import { useEffect } from 'react';
import { getImpactEvulations, hasImpactEvaluationsToNotify, markImpactEvaluationsAsNotified } from 'services/api';
import { getProfileInfo } from 'services/utils';
import { EvaluationType } from 'types/evaluation';
import { useHasStudentImpactEvaluations } from 'pctHooks/useHasImpactEvaluations';

interface NotificationConfig {
    showNotification: boolean;
    notificationType?: string;
    trainingTitle?: string;
    daysToComplete?: number;
    availableAfterDays?: number;
    customData?: any;
    modalConfig?: {
        showCheckbox?: boolean;
        checkboxLabel?: string;
        checkboxChecked?: boolean;
        onCheckboxChange?: (checked: boolean) => void;
    };
}

/**
 * Hook para verificar e exibir notificações de avaliações de impacto pendentes
 * Este hook é executado uma vez quando o componente é montado para verificar
 * se há avaliações de impacto pendentes que devem ser notificadas ao usuário
 *
 * @param setNotificationState - Função para atualizar o estado da notificação
 * @param evaluationType - Tipo de avaliação (IMPACT ou STUDENT_IMPACT)
 */
export const usePendingImpactEvaluationNotification = (
    setNotificationState: (state: NotificationConfig | ((prev: NotificationConfig) => NotificationConfig)) => void,
    evaluationType: EvaluationType.IMPACT | EvaluationType.STUDENT_IMPACT = EvaluationType.STUDENT_IMPACT,
) => {
    const idUser = getProfileInfo()?.idUser;
    const { hasEvaluations } = useHasStudentImpactEvaluations(idUser || 0);

    useEffect(() => {
        const checkPendingEvaluationsForNotification = async () => {
            if (!idUser) return;

            if (!hasEvaluations) {
                return;
            }
 
            const [navigationEntry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
            if (!navigationEntry || navigationEntry.type === 'reload') {
                return;
            }

            const response = await hasImpactEvaluationsToNotify(idUser);
            if (response?.data?.result) {
                const evaluationsResponse = await getImpactEvulations(idUser, {
                    evaluationType,
                    onlyPending: false,
                });



                if (evaluationsResponse.data && evaluationsResponse.data.length > 0) {
                    const daysToComplete = evaluationsResponse.data[0]?.daysToComplete || 30;
                    const availableAfterDays = evaluationsResponse.data[0]?.availableAfterDays || 30;
                    
                    const createNotificationState = () => {
                        let checkboxChecked = false;
                        
                        return {
                            showNotification: true,
                            notificationType: 'pending-impact-evaluation',
                            trainingTitle: evaluationsResponse.data[0]?.title || 'Avaliação de Impacto',
                            daysToComplete,
                            availableAfterDays,
                            modalConfig: {
                                showCheckbox: true,
                                checkboxLabel: 'Não mostrar esta notificação novamente',
                                checkboxChecked: false,
                                onCheckboxChange: (checked: boolean) => {
                                    checkboxChecked = checked;
                                },
                            },
                            customData: {
                                evaluations: evaluationsResponse.data,
                                onButtonClick: () => { 
                                    setNotificationState((prev) => ({ ...prev, showNotification: false }));
                                },
                                onButtonClickWithCheckbox: async (isCheckboxChecked: boolean) => {
                                    if (isCheckboxChecked) { 
                                        try {
                                            await markImpactEvaluationsAsNotified(idUser);
                                            setNotificationState((prev) => ({ ...prev, showNotification: false }));
                                        } catch (error) {
                                            setNotificationState((prev) => ({ ...prev, showNotification: false }));
                                        }
                                    } else { 
                                        setNotificationState((prev) => ({ ...prev, showNotification: false }));
                                    }
                                },
                                onClickHighlight: () => {
                                    setNotificationState((prev) => ({ ...prev, showNotification: false }));
                                    window.location.hash = '#/myJourney/impactEvaluation';
                                },
                            },
                        };
                    };

                    setNotificationState(createNotificationState());
                }
            }
        };

        checkPendingEvaluationsForNotification();
    }, [idUser, evaluationType, setNotificationState, hasEvaluations]);

    return null;
};

/**
 * Hook específico para notificações de avaliações de impacto de estudantes
 */
export const usePendingStudentImpactEvaluationNotification = (
    setNotificationState: (state: NotificationConfig | ((prev: NotificationConfig) => NotificationConfig)) => void,
) => {
    return usePendingImpactEvaluationNotification(setNotificationState, EvaluationType.STUDENT_IMPACT);
};

/**
 * Hook específico para notificações de avaliações de impacto de gestores
 */
export const usePendingManagerImpactEvaluationNotification = (
    setNotificationState: (state: NotificationConfig | ((prev: NotificationConfig) => NotificationConfig)) => void,
) => {
    return usePendingImpactEvaluationNotification(setNotificationState, EvaluationType.IMPACT);
};

export default usePendingImpactEvaluationNotification;
export type { NotificationConfig };
